import React, { useEffect, useState, useCallback } from "react";
import { MapPin, Navigation, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { BusinessCard } from "@/components/BusinessCard";
import { CardsSkeleton } from "@/components/CardsSkeleton";
import { Button } from "@/components/ui/button";

export default function NearMe() {
  useSeo("Near Me | OyeBest", "Find the best businesses and services near your current location.");
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | locating | denied | ready
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const locate = useCallback(() => {
    if (!navigator.geolocation) { setStatus("denied"); return; }
    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setStatus("ready"); },
      () => { setStatus("denied"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const fetchNear = useCallback(() => {
    if (!coords) return;
    setLoading(true);
    const city = localStorage.getItem("oyebest_city") || "etah";
    const params = { city, sort: "nearest", lat: coords.lat, lng: coords.lng };
    if (active) params.category = active;
    api.get("/businesses", { params }).then((r) => setList(r.data)).finally(() => setLoading(false));
  }, [coords, active]);

  useEffect(() => { fetchNear(); }, [fetchNear]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wider"><Navigation className="w-4 h-4" /> Near Me</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Best Places Near You</h1>
      <p className="text-muted-foreground mt-1.5">Location ki permission dijiye aur apne aas-paas ki best jagah dhoondhiye.</p>

      {status !== "ready" && (
        <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-8 text-center">
          {status === "denied" ? (
            <>
              <AlertCircle className="w-9 h-9 text-amber-500 mx-auto mb-3" />
              <p className="font-semibold">Location access needed</p>
              <p className="text-sm text-muted-foreground mt-1">Please allow location permission in your browser, then try again.</p>
            </>
          ) : (
            <>
              <MapPin className="w-9 h-9 text-primary mx-auto mb-3" />
              <p className="font-semibold">Enable your location</p>
              <p className="text-sm text-muted-foreground mt-1">We'll show businesses sorted by distance from you.</p>
            </>
          )}
          <Button onClick={locate} className="mt-5 rounded-full gap-2" data-testid="nearme-locate-btn">
            {status === "locating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            {status === "locating" ? "Locating…" : "Use My Location"}
          </Button>
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-6 pb-1">
            <button onClick={() => setActive("")} className={`px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap border ${active === "" ? "bg-primary text-white border-primary" : "bg-white border-border text-muted-foreground"}`} data-testid="nearme-cat-all">All</button>
            {categories.map((c) => (
              <button key={c.slug} onClick={() => setActive(c.slug)} className={`px-4 h-9 rounded-full text-sm font-medium whitespace-nowrap border ${active === c.slug ? "bg-primary text-white border-primary" : "bg-white border-border text-muted-foreground"}`} data-testid={`nearme-cat-${c.slug}`}>{c.name}</button>
            ))}
          </div>
          <div className="mt-6">
            {loading ? <CardsSkeleton /> : list.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">No businesses found nearby.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {list.map((b, i) => <BusinessCard key={b.id} business={b} index={i} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
